import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface MovieCardProps {
  movie: Movie;
  variant?: "default" | "compact" | "wide";
}

export default function MovieCard({ movie, variant = "default" }: MovieCardProps) {
  const isCompact = variant === "compact";
  const isWide = variant === "wide";

  if (isWide) {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <div className={`${isCompact ? "w-24 h-32" : "w-28 h-36"} shrink-0 rounded-md overflow-hidden`}>
          <img
            src={movie.coverImage}
            alt={movie.title}
            title={`${movie.title} - 高清在线观看`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
          <div>
            <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
              {movie.title}
            </h3>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded bg-accent-100 text-accent-700 whitespace-nowrap">
                {movie.category}
              </span>
              <span className="text-xs text-foreground-500">{movie.year}</span>
              <span className="text-xs text-foreground-500">{movie.duration}</span>
            </div>
            <p className="text-xs text-foreground-600 mt-2 line-clamp-2">{movie.summary}</p>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <span className="flex items-center gap-1 text-xs text-accent-600 font-medium">
              <i className="ri-star-fill text-accent-500 text-xs"></i>
              {movie.rating}
            </span>
            <span className="text-xs text-foreground-400">
              <i className="ri-eye-line mr-1"></i>
              {(movie.viewCount / 10000).toFixed(1)}万次播放
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block cursor-pointer"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <div className={`relative ${isCompact ? "aspect-[3/4]" : "aspect-[3/4]"} rounded-lg overflow-hidden bg-background-200`}>
        <img
          src={movie.coverImage}
          alt={movie.title}
          title={`${movie.title} - 高清在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <p className="text-white text-xs line-clamp-2">{movie.summary}</p>
            <div className="flex items-center justify-center mt-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/90 text-white">
                <i className="ri-play-fill text-sm ml-0.5"></i>
              </span>
            </div>
          </div>
        </div>
        {movie.isNew && (
          <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded bg-accent-500 text-background-50 font-medium whitespace-nowrap">
            NEW
          </span>
        )}
        {movie.isHot && !movie.isNew && (
          <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded bg-primary-500 text-background-50 font-medium whitespace-nowrap">
            热播
          </span>
        )}
        <div className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white font-medium whitespace-nowrap">
          {movie.rating}
        </div>
      </div>
      <div className="mt-2.5">
        <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
          {movie.title}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 text-xs text-foreground-500 flex-wrap">
          <span>{movie.year}</span>
          <span>·</span>
          <span>{movie.category}</span>
          <span>·</span>
          <span>{movie.duration}</span>
        </div>
      </div>
    </Link>
  );
}